import type {
  QuestionnaireAnalysisStatus,
  QuestionnaireAnswerValue,
  QuestionnaireQuestion,
  QuestionnaireResponse,
} from '../../../infrastructure/QuestionnaireClient';

export interface QuestionnaireOptionStatistic {
  value: string;
  label: string;
  count: number;
  percentage: number;
}

export interface QuestionnaireTextSample {
  responseId: number;
  submittedAt: string;
  value: string;
}

export interface QuestionnaireQuestionStatistic {
  questionId: string;
  label: string;
  type: string;
  answeredCount: number;
  options: QuestionnaireOptionStatistic[];
  samples: QuestionnaireTextSample[];
}

export interface QuestionnaireStatistics {
  totalResponses: number;
  statusCounts: Record<QuestionnaireAnalysisStatus, number>;
  questions: QuestionnaireQuestionStatistic[];
}

const EMPTY_STATUS_COUNTS: Record<QuestionnaireAnalysisStatus, number> = {
  pending: 0,
  forwarded: 0,
  failed: 0,
};

const TEXT_SAMPLE_LIMIT = 5;

const isAnswered = (answer: QuestionnaireAnswerValue | undefined): boolean => {
  if (answer === undefined) {
    return false;
  }
  if (Array.isArray(answer)) {
    return answer.length > 0;
  }
  if (typeof answer === 'string') {
    return answer.trim().length > 0;
  }
  return true;
};

const answerValues = (answer: QuestionnaireAnswerValue | undefined): string[] => {
  if (!isAnswered(answer)) {
    return [];
  }
  if (Array.isArray(answer)) {
    return answer.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  }
  return [String(answer)];
};

const optionLabel = (question: QuestionnaireQuestion, value: string): string => {
  return question.options?.find((option) => option.value === value)?.label ?? value;
};

const percentage = (count: number, total: number): number => {
  if (total <= 0) {
    return 0;
  }
  return Math.round((count / total) * 100);
};

export const buildQuestionnaireStatistics = (
  questions: QuestionnaireQuestion[],
  responses: QuestionnaireResponse[],
): QuestionnaireStatistics => {
  const statusCounts = responses.reduce<Record<QuestionnaireAnalysisStatus, number>>(
    (counts, response) => ({
      ...counts,
      [response.analysisStatus]: counts[response.analysisStatus] + 1,
    }),
    { ...EMPTY_STATUS_COUNTS },
  );

  const questionStats = questions.map<QuestionnaireQuestionStatistic>((question) => {
    const answers = responses
      .map((response) => ({
        response,
        answer: response.answers.find((item) => item.questionId === question.questionId)?.answer,
      }))
      .filter(({ answer }) => isAnswered(answer));

    const answeredCount = answers.length;
    const optionCounts = new Map<string, number>();
    const samples: QuestionnaireTextSample[] = [];
    const isChoiceQuestion = question.type === 'single_select' || question.type === 'multi_select';

    answers.forEach(({ response, answer }) => {
      if (isChoiceQuestion) {
        answerValues(answer).forEach((value) => {
          optionCounts.set(value, (optionCounts.get(value) ?? 0) + 1);
        });
        return;
      }

      const values = answerValues(answer);
      if (values.length > 0) {
        samples.push({
          responseId: response.id,
          submittedAt: response.submittedAt,
          value: values.join(', '),
        });
      }
    });

    const configuredOptions = question.options ?? [];
    const optionValues = new Set([...configuredOptions.map((option) => option.value), ...optionCounts.keys()]);
    const options = Array.from(optionValues).map<QuestionnaireOptionStatistic>((value) => {
      const count = optionCounts.get(value) ?? 0;
      return {
        value,
        label: optionLabel(question, value),
        count,
        percentage: percentage(count, answeredCount),
      };
    });

    return {
      questionId: question.questionId,
      label: question.label ?? question.questionId,
      type: question.type ?? 'free_text',
      answeredCount,
      options,
      samples: samples
        .sort((left, right) => new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime())
        .slice(0, TEXT_SAMPLE_LIMIT),
    };
  });

  return {
    totalResponses: responses.length,
    statusCounts,
    questions: questionStats,
  };
};
